import { useQuery } from "@tanstack/react-query";
import { useServices } from "../services";
import { isApiError } from "@clubcore/api";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { useT } from "../../i18n/I18nProvider";
import { Button } from "../../ui/Button";
import { TextField } from "../../ui/Field";
import { EMAIL_PATTERN } from "../../ui/form";
import { useErrorMessage } from "../../ui/feedback";
import { useAuth } from "../auth";
import { PRODUCT_NAME } from "../club";
import { landingFor, returnPathFor, type ReturnState } from "../landing";
import { useDocumentTitle } from "../useDocumentTitle";

export function SignInPage() {
  const t = useT();
  const { api } = useServices();
  const microsoft = useQuery({ queryKey: ["microsoft-signin"], queryFn: () => api.auth.microsoftStatus(), retry: false });
  const describe = useErrorMessage();
  const { state, signIn } = useAuth();
  const location = useLocation();
  const returnState = location.state as ReturnState | null;
  const expired = returnState?.expired ?? false;
  const { clubSelection } = useServices();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useDocumentTitle(t("auth.signIn"));

  // Where to go is decided here, once, from who just signed in: the page they were sent from only if it is theirs and allowed.
  if (state.status === "signedIn") {
    return <Navigate to={landingFor(state.me, clubSelection.getSlug(), returnPathFor(returnState, state.me.user.id))} replace />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const next: typeof errors = {};
    if (!email.trim()) next.email = t("validation.required");
    else if (!EMAIL_PATTERN.test(email.trim())) next.email = t("validation.email");
    if (!password) next.password = t("validation.required");
    setErrors(next);
    setFormError(null);
    if (next.email || next.password) return;
    setBusy(true);
    try {
      await signIn(email.trim(), password); // the redirect above takes over once the session is in place
    } catch (error) {
      setFormError(isApiError(error) && error.kind === "unauthorized" ? t("auth.invalid") : describe(error));
      setBusy(false);
    }
  }

  return (
    <div className="cc-center">
      <main className="cc-auth-card">
        <div className="cc-logo-wrap">
          <img className="cc-logo" src="/brand/clubcore-logo.webp" alt={PRODUCT_NAME} width={176} height={176} />
        </div>
        <h1>{t("auth.signInTitle")}</h1>
        {expired ? (
          <p className="cc-alert" role="status">
            {t("auth.expired")}
          </p>
        ) : null}
        {microsoft.data?.enabled ? (
          <div className="cc-form">
            <Button variant="primary" busy={busy} onClick={() => {
              setBusy(true);
              setFormError(null);
              void api.auth.startMicrosoft(returnState?.userId ? "/" : (returnState?.from ?? "/")).then(({ authorization_url }) => {
                window.location.assign(authorization_url);
              }).catch((error: unknown) => { setFormError(describe(error)); setBusy(false); });
            }}>{t("auth.microsoft")}</Button>
            <h2>{t("auth.localAdmin")}</h2>
            <p className="cc-muted">{t("auth.localAdminHint")}</p>
          </div>
        ) : null}
        <form className="cc-form" onSubmit={(event) => void submit(event)} noValidate>
          {formError ? (
            <p className="cc-alert" role="alert">
              {formError}
            </p>
          ) : null}
          <TextField label={t("auth.email")} type="email" autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} error={errors.email} required />
          <TextField label={t("auth.password")} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={errors.password} required />
          <Button type="submit" variant="primary" busy={busy}>
            {t("auth.signIn")}
          </Button>
        </form>
        <p className="cc-auth-extra">
          <Link to="/reset-password">{t("auth.haveResetCode")}</Link>
        </p>
      </main>
    </div>
  );
}
